<template>
  <div>
    <reports-filter
      v-if="isSidebarVisible"
      is-company-field-required
      is-installation-field-required
      is-asset-field-required
      initialize-filter
      display-fields-in-line
      :filter-objects.sync="companyFilter"
      @user-companies-have-regional="setUserCompaniesHaveRegional"
    />
    <tag-options-input
      class="mb-4"
      is-required-field
      :label="$t('alarms-and-warnings.filter.alarm-classifications')"
      :tag-label="'name'"
      :track-tag-by="'id'"
      :tags="alarmsClassificationsLookup"
      :selected-items.sync="generalFilter.selectedAlarmClassifications"
      :options-custom-label="$t('alarms-and-warnings.filter.all-classifications')"
    />
    <tag-options-input
      class="mb-3"
      is-required-field
      :label="$t('alarms-and-warnings.filter.installation-status')"
      :tag-label="'name'"
      :track-tag-by="'id'"
      :tags="installationStatusLookup"
      :selected-items.sync="generalFilter.selectedInstallationStatus"
      :options-custom-label="$t('alarms-and-warnings.filter.all-installation-status')"
    />
    <tag-options-input
      class="mb-3"
      is-required-field
      :label="$t('alarms-and-warnings.filter.alarm-status')"
      :tag-label="'name'"
      :track-tag-by="'id'"
      :tags="alarmsStatusLookup"
      :selected-items.sync="generalFilter.selectedAlarmStatus"
      :options-custom-label="$t('alarms-and-warnings.filter.all-alarms-status')"
    />
    <period-date-select
      class="alert-warning-report__period-select"
      has-all-periods
      period-label="alarms-and-warnings.filter.period"
      :additional-periods="additionalPeriods"
      :personalized="false"
      :horizontalArrangement="false"
      :period.sync="generalFilter.period"
    />
  </div>
</template>

<script>
import TagOptionsInput from '@/components/inputs/TagOptionsInput';
import ReportsFilter from '@/components/ReportsFilter';
import PeriodDateSelect from '@/components/PeriodDateSelect';
import { alarmsAndWarningsStatusLookup }
  from '@/domain/data/generalPanel/reports/alarmsAndWarnings/alarmsAndWarningsStatusLookup';
import alarmsClassificationsLookup
  from '@/domain/data/generalPanel/reports/alarmsAndWarnings/alarmsClassificationsLookup';
import installationStatusLookup
  from '@/domain/data/generalPanel/reports/alarmsAndWarnings/alarmsInstallationsStatusLookup';
import periods from '@/domain/enums/periods-dropdown-filter';
import { required } from 'vuelidate/lib/validators';

export default {
  components: {
    ReportsFilter,
    TagOptionsInput,
    PeriodDateSelect,
  },
  props: {
    isSidebarVisible: {
      type: Boolean,
      default: false,
    },
    filter: {
      type: Object,
      default: () => ({
        companyFilter: {
          companies: [],
          regional: [],
          installations: [],
          assets: [],
        },
        generalFilter: {
          selectedAlarmStatus: [],
          selectedAlarmClassifications: [],
          selectedInstallationStatus: [],
          period: -1,
        },
      }),
    },
  },
  data: () => ({
    alarmsStatusLookup: [],
    alarmsClassificationsLookup: [],
    installationStatusLookup: [],
    userCompaniesHaveRegional: true,
    companyFilter: {
      companies: [],
      regional: [],
      installations: [],
      assets: [],
    },
    generalFilter: {
      selectedAlarmStatus: [],
      selectedAlarmClassifications: [],
      selectedInstallationStatus: [],
      period: periods.ALL,
    },
    additionalPeriods: [
      periods.ONE_HUNDRED_EIGHTY,
      periods.THREE_HUNDRED_SIXTY_FIVE,
    ],
  }),
  validations: {
    generalFilter: {
      selectedAlarmStatus: {
        required,
      },
      selectedAlarmClassifications: {
        required,
      },
      selectedInstallationStatus: {
        required,
      },
      period: {
        required,
      },
    },
    companyFilter: {
      companies: {
        required,
      },
      installations: {
        required,
      },
      assets: {
        required,
      },
    },
  },
  computed: {
    selectedFilter() {
      return {
        companyFilter: this.companyFilter,
        generalFilter: this.generalFilter,
      };
    },
  },
  methods: {
    setUserCompaniesHaveRegional(userCompaniesHaveRegional) {
      this.userCompaniesHaveRegional = userCompaniesHaveRegional;
    },
    onSearchButtonClick() {
      if (this.$v.$invalid) {
        this.reportErrors();
        return;
      }

      this.$emit('search', this.selectedFilter);
    },
    reportErrors() {
      const invalidFields = [];

      if (this.$v.companyFilter.companies.$invalid) {
        invalidFields.push(this.$t('entities.company'));
      }

      if (this.$v.companyFilter.installations.$invalid) {
        invalidFields.push(this.$t('entities.installation'));
      }

      if (this.$v.companyFilter.assets.$invalid) {
        invalidFields.push(this.$t('entities.asset'));
      }

      if (this.$v.generalFilter.selectedAlarmClassifications.$invalid) {
        invalidFields.push(this.$t('entities.classification'));
      }

      if (this.$v.generalFilter.selectedInstallationStatus.$invalid) {
        invalidFields.push(this.$t('entities.installation-status'));
      }

      if (this.$v.generalFilter.selectedAlarmStatus.$invalid) {
        invalidFields.push(this.$t('entities.alarm-status'));
      }

      if (this.$v.generalFilter.period.$invalid) {
        invalidFields.push(this.$t('entities.period'));
      }

      this.$pushAlert({
        code: 'MA001',
        messageParams: {
          NomeDoCampo: invalidFields.join(', '),
        },
      });
    },
    initializeFilter() {
      this.alarmsStatusLookup = alarmsAndWarningsStatusLookup(this);
      this.alarmsClassificationsLookup = alarmsClassificationsLookup(this);
      this.installationStatusLookup = installationStatusLookup(this);

      this.setGeneralFilter();
      this.setCompanyFilter();
    },
    setGeneralFilter() {
      this.generalFilter.selectedAlarmStatus = this.filterByList(
        this.alarmsStatusLookup,
        this.filter.generalFilter.selectedAlarmStatus,
      );

      this.generalFilter.selectedAlarmClassifications = this.filterByList(
        this.alarmsClassificationsLookup,
        this.filter.generalFilter.selectedAlarmClassifications,
      );

      this.generalFilter.selectedInstallationStatus = this.filterByList(
        this.installationStatusLookup,
        this.filter.generalFilter.selectedInstallationStatus,
      );

      this.generalFilter.period = this.filter.generalFilter.period;
    },
    setCompanyFilter() {
      this.companyFilter = {
        ...this.companyFilter,
        ...this.filter.companyFilter,
      };
    },
    filterByList(list, filterList) {
      if (filterList.length) {
        return list.filter(item => filterList.map(x => x.id?.toUpperCase())
          .includes(item.id?.toUpperCase()));
      }

      return list;
    },
  },
};
</script>

<style lang="sass" scoped>
.filter-content
  position: absolute
  display: flex
  -webkit-box-orient: vertical
  -webkit-box-direction: normal
  flex-flow: column
  width: calc(100% - 30px)
  height: 100%

  &__fields
    flex-grow: 1
    overflow-y: auto

    ::v-deep
      & .multiselect__tags-wrap
        overflow-x: hidden

  &__search-button
    margin-top: 1rem

    .col
      display: flex
      justify-content: flex-end
</style>
